import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild, ViewContainerRef} from '@angular/core';
import {Router} from "@angular/router";
import {Subscription} from 'rxjs';

import {CHAR_STRUCT, CHAR_TYPE, OPTION_TYPE, SEUNGJO_DISP_TYPE} from '../../defines/client.const';
import {SASEUNG_DISP_LIST, SASEUNG_SYMBOL_LIST} from '../../defines/gukeo.const';
import {IsEmptyVar} from '../../defines/common.func';
import {splitCorrect} from "../../defines/gukeo.func";
import {SimpleWord} from '../../models/simple-word.model';
import {OptionService} from '../../services/option/option.service';
import {WordHistoryService} from "../../services/word/word-history.service";
import {multiLinkMixin} from "../../mixin/multi-link.mixin";
import {GlobalEventService} from "../../services/global-event/global-event.service";
import {DynamicComponentService} from "../../services/dynamic-component/dynamic-component.service";


@Component({
  selector: 'app-word-disp',
  templateUrl: `./word-disp.component.html`,
  styleUrls: ['./word-disp.component.scss'],
})
export class WordDispComponent extends multiLinkMixin implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('container', {read: ViewContainerRef}) container?: ViewContainerRef;
  @Input() word: SimpleWord | null = null;
  @Input() class: string | null = null;
  @Input() showLevel = true;
  @Input() linkPopup = false;
  @Input() large = false;
  @Input() fullWord = false;
  @Input() hanjaLink = false; // 한자 링크 팝업이 뜨도록 하는 옵션
  @Input() wrapHanja = false; // 공간 부족 시 한글/한자를 별도 줄로 분리
  @Input() wrapReservedWidth = 0; // 절대배치된 우측 버튼 등으로 예약해야 할 폭
  @Input() popupContainer?: ViewContainerRef; // 링크 팝업을 열 기준 컨테이너
  @Input() popupLeftOffset = 0; // 링크 팝업 수평 보정값
  @Input() popupZIndex = 0; // 링크 팝업 host 우선순위

  /** 실제로 줄 분리가 필요한지 (내부 상태) */
  _shouldWrap = false;
  private _wrapTimerId: ReturnType<typeof setTimeout> | null = null;
  private _isDestroyed = false;
  private optionSub?: Subscription;

  /** .row.top에 적용할 클래스 — 한글 성조와 한자 사성만, 장단 기호는 제외 */
  public GetTopRowClass(cs: CHAR_STRUCT): string {
    if (cs.type === CHAR_TYPE.JANGDAN) return '';
    return this.GetSeungjoClass(cs);
  }

  // TODO: option.showSaseung()을 async 함수로 변환해서 앱 속도를 높일것.
  get showSaseung(): boolean {
    // console.log('showSaseung :', this.option.showSaseung);
    return this.option.showSaseung;
  }

  get seungjoDispType(): SEUNGJO_DISP_TYPE {
    return this.option.seungjoDispType;
  }

  public get width(): number {
    return this.elRef.nativeElement.offsetWidth;
  }

  get effectiveWrap(): boolean {
    return this.wrapHanja && this._shouldWrap;
  }

  constructor(
    public override router: Router,
    public override wHistorySvc: WordHistoryService,
    public override dynamicSvc: DynamicComponentService,
    protected option: OptionService,
    private elRef: ElementRef,
    private globalEventSvc: GlobalEventService,
    private cdr: ChangeDetectorRef,
  ) {
    super(router, wHistorySvc, dynamicSvc);
  }

  ngAfterViewInit() {
    this.optionSub = this.option.getState().subscribe(type => {
      if (type === OPTION_TYPE.LARGE_MODE) {
        this.scheduleEvaluateWrap();
      }
    });
    this.scheduleEvaluateWrap();
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes['word'] && !changes['word'].firstChange) || changes['wrapReservedWidth']) {
      this._shouldWrap = false;
      // microtask로 DOM 복원 후 측정 (CD 재진입 방지)
      Promise.resolve().then(() => {
        if (this._isDestroyed) return;
        this.cdr.detectChanges();
        this.scheduleEvaluateWrap();
      });
    }
  }

  ngOnDestroy() {
    this._isDestroyed = true;
    this.optionSub?.unsubscribe();
    this.clearWrapTimer();
  }

  private scheduleEvaluateWrap() {
    this.clearWrapTimer();
    this._wrapTimerId = setTimeout(() => this.evaluateWrap());
  }

  private clearWrapTimer() {
    if (this._wrapTimerId !== null) {
      clearTimeout(this._wrapTimerId);
      this._wrapTimerId = null;
    }
  }

  /** 단일 줄 렌더링 후 넘침을 감지하여 줄 분리 여부 결정 */
  private evaluateWrap() {
    if (!this.wrapHanja || !this.word || this.hanjaChars.length === 0) {
      return;
    }

    const el = this.elRef.nativeElement as HTMLElement;
    const wrap = el.querySelector('.keyword-wrap') as HTMLElement | null;
    const parent = el.parentElement as HTMLElement | null;
    const containerWidth = Math.max(el.clientWidth || 0, parent?.clientWidth || 0);
    const availableWidth = Math.max(
      0,
      containerWidth - this.wrapReservedWidth
    );

    if (wrap && availableWidth > 0) {
      const shouldWrap = wrap.scrollWidth > availableWidth;
      if (this._shouldWrap !== shouldWrap) {
        this._shouldWrap = shouldWrap;
        this.cdr.detectChanges();
      }
    }
  }

  @HostListener('click', ['$event'])
  onClick($event: MouseEvent) {
    console.log('WordDispComponent.onClick()', this.word);

    if (this.linkPopup) {
      $event.preventDefault();
      $event.stopPropagation();
      this.globalEventSvc.Click($event, true);

      if (!this.word?.id) {
        throw new Error('WordDispComponent.onClick() : word.id is null');
      }
      const container = this.popupContainer ?? this.container;
      if (!container) {
        throw new Error('WordDispComponent.onClick() : container is null');
      }
      this.OpenMultiLink(this.word.id, container, null, this.popupLeftOffset, this.popupZIndex).subscribe({
        error: (err) => console.error('WordDispComponent: failed to open link popup', err),
      });
    }
  }

  public GetSeungjoClass(cs: CHAR_STRUCT): string {
    // console.log('GetSeungjoClass()', cs);

    switch (cs.type) {
      case CHAR_TYPE.HANJA:
        return 'cn ' + ((this.showSaseung && cs.saseung !== undefined) ? SASEUNG_SYMBOL_LIST[cs.saseung] : '');
      case CHAR_TYPE.JANGDAN:
        if (['/', ':', ' '].includes(cs.char)) {
          return 'small';
        }
        else {
          return 'middle';
        }
    }
    return '';
  }

  public IsShowSeungjo(cs: CHAR_STRUCT): boolean {
    return cs.type === CHAR_TYPE.HANGUL && (
      1 <= cs.seungjo! && cs.seungjo! <= 5);
  }

  public GetSeungjoClass2(cs: CHAR_STRUCT): string {
    if (cs.type === CHAR_TYPE.HANGUL) {
      return 'ic-intonation-' + cs.seungjo;
    }
    return '';
  }

  // 허용되는 성조
  // TODO: 허용되는 성조가 3일 때에는 표시되는 내용이 없다. 바탕색을 변경해야 하나?
  public GetSeungjo2Class(cs: CHAR_STRUCT): string {
    if (cs.type === CHAR_TYPE.HANGUL && !IsEmptyVar(cs.seungjo2)) {
      return 'word-' + cs.seungjo2;
    }
    return '';
  }

  // 앱에서는 사용하던 함수. 책상형에서는 사용하지 않음.
  // public GetSaseungTxt(cs: CHAR_STRUCT): string {
  //   // console.log('GetSaseungTxt()', cs);
  //   return (this.showSaseung && cs.type === CHAR_TYPE.HANJA && cs.saseung !== undefined) ? SASEUNG_DISP_LIST[cs.saseung] : '';
  // }

  public GetCharClass(cs: CHAR_STRUCT): string {
    switch (cs.type) {
      case CHAR_TYPE.HANJA:
        return 'cn';
      case CHAR_TYPE.JANGDAN:
        if (cs.char === '/') {
          return 'small type-2';
        }
        else if ([':', ' '].includes(cs.char)) {
          return 'small';
        }
        else {
          return 'middle';
        }
    }
    return '';
  }

  public GetCharTxt(cs: CHAR_STRUCT): string {
    return cs.type === CHAR_TYPE.JANGDAN && cs.char === '/' ? '' : cs.char;
  }

  public GetHightlightClass(idx: number): string {
    if (!this.word || !this.word.highlight) {
      return '';
    }

    const highlight = this.word.highlight[idx];
    switch (highlight) {
      case '1':
        return 'highlight';
      case '2':
        return 'highlight2';
      default:
        return '';
    }
  }

  public get arrowCorrect(): string {
    const [arrow, word] = this.word?.correct ? splitCorrect(this.word?.correct) : [undefined, undefined];
    return arrow ?? '';
  }


  public get wordCorrect(): string | null {
    const [arrow, word] = this.word?.correct ? splitCorrect(this.word?.correct) ?? ['', null] : ['', null];
    return word;
  }

  protected IsHanja(cs: CHAR_STRUCT): boolean {
    return cs.type === CHAR_TYPE.HANJA;
  }

  /** wrapHanja=true일 때 한글/장단 글자만 (원본 인덱스 포함) */
  get hangulChars(): { cs: CHAR_STRUCT; idx: number }[] {
    return this.word?.wordStruct
      ?.map((cs, idx) => ({ cs, idx }))
      .filter(item => item.cs.type !== CHAR_TYPE.HANJA) ?? [];
  }

  /** wrapHanja=true일 때 한자 글자만 (원본 인덱스 포함) */
  get hanjaChars(): { cs: CHAR_STRUCT; idx: number }[] {
    return this.word?.wordStruct
      ?.map((cs, idx) => ({ cs, idx }))
      .filter(item => item.cs.type === CHAR_TYPE.HANJA) ?? [];
  }

}
